import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/stores/sidebar.store';
import { useAuthStore } from '@/stores/auth.store';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getScopedProfile, isPathAllowed } from '@/lib/scopedAccess';
import { isPathModuleEnabled } from '@/lib/modules';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { CommandPalette } from '../shared/CommandPalette';
import { ToastContainer } from '../shared/ToastContainer';
import { TopLoader } from '../shared/TopLoader';
import { OfflineBanner } from '../shared/OfflineBanner';
import { KeyboardShortcutsHelp } from '../shared/KeyboardShortcutsHelp';
import { ErrorBoundary } from '../shared/ErrorBoundary';
import { RouteAnnouncer } from '../shared/RouteAnnouncer';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useNotifications } from '@/hooks/useNotifications';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { useFaviconBadge } from '@/hooks/useFaviconBadge';
import { useNotificationStore } from '@/stores/notification.store';
import { IdleWarningDialog } from '../shared/IdleWarningDialog';
import { useRealtimeNotifications, useWhatsAppIncoming, type WsWhatsAppIncoming } from '@/hooks/useSocket';
import { toast } from '@/stores/toast.store';

export function AppLayout() {
  const isCollapsed = useSidebarStore((s) => s.isCollapsed);
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  // Restricted (scoped) roles — and partner logins — are confined to their allowed route prefixes; a
  // direct URL to any other page redirects to their home. Normal roles get a null profile and are
  // never affected.
  const scoped = getScopedProfile(user?.permissions, user?.partnerId);
  const blocked = scoped ? !isPathAllowed(location.pathname, scoped) : false;
  // Block routes whose owning module is not licensed for this tenant. Scoped
  // roles are already confined by their profile above; layering module gating on
  // top can ping-pong between the two guards, so skip it for scoped users.
  const moduleBlocked = !scoped && !isPathModuleEnabled(user?.enabledModules, location.pathname);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const handleOpenHelp = useCallback(() => setShowShortcuts((v) => !v), []);
  useKeyboardShortcuts(handleOpenHelp);
  useNotifications();
  const unreadCount = useNotificationStore((s) => s.unreadCount());
  useFaviconBadge(unreadCount);
  const { showWarning, secondsLeft, dismissWarning } = useIdleTimeout();
  // Connect WebSocket and listen for real-time notifications
  useRealtimeNotifications();

  // Global alert for inbound WhatsApp messages — bell entry always, plus a toast
  // unless the agent is already viewing the WhatsApp inbox. pathRef avoids
  // re-subscribing the socket handler on every route change.
  const pathRef = useRef(location.pathname);
  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);
  const onWhatsAppIncoming = useCallback((p: WsWhatsAppIncoming) => {
    const who = p.customerName || p.phone;
    if (!pathRef.current.startsWith('/whatsapp/inbox')) {
      toast.info(`New WhatsApp from ${who}`);
    }
    useNotificationStore.getState().addNotification({
      type: 'info',
      category: 'ticket',
      title: `WhatsApp: ${who}`,
      message: p.preview || 'New message',
      href: '/whatsapp/inbox',
    });
  }, []);
  useWhatsAppIncoming(onWhatsAppIncoming);

  const handleIdleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  return (
    <div className="min-h-screen bg-background">
      {/* Slim progress bar for route transitions */}
      <TopLoader />

      {/* Skip to content — visible on Tab focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none"
      >
        Skip to main content
      </a>

      <RouteAnnouncer />
      <Sidebar />
      <CommandPalette />
      <ToastContainer />
      <KeyboardShortcutsHelp open={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <IdleWarningDialog
        open={showWarning}
        secondsLeft={secondsLeft}
        onStay={dismissWarning}
        onLogout={handleIdleLogout}
      />
      <div
        className={cn(
          'flex flex-col transition-all duration-200',
          // No margin on mobile (sidebar is overlay); margin on desktop
          isCollapsed ? 'lg:ml-sidebar-collapsed' : 'lg:ml-sidebar'
        )}
      >
        <ImpersonationBanner />
        <Topbar />
        <OfflineBanner />
        <main id="main-content" className="flex-1 p-4 sm:p-6" role="main">
          <div className="mx-auto max-w-7xl animate-page-in">
            <ErrorBoundary resetKey={location.pathname}>
              {blocked && scoped ? (
                <Navigate to={scoped.home} replace />
              ) : moduleBlocked ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Outlet />
              )}
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
